import { useState } from 'react';
import { iconeDe } from './apparence';

const FILES_BASE_URL = import.meta.env.VITE_FILES_BASE_URL || 'https://www.cabaza.be/files';

/**
 * Des contenus alternatifs, un seul visible à la fois.
 *
 * ========================================================================
 * POURQUOI CE BLOC EXISTE
 * ========================================================================
 *
 * La page d'installation décrit TROIS procédures incompatibles — iPhone,
 * Android, ordinateur. Les empiler les unes sous les autres obligerait
 * chaque lecteur à traverser deux méthodes qui ne le concernent pas, et la
 * plus probable serait qu'il suive la mauvaise.
 *
 * ========================================================================
 * CE QU'IL NE FAUT PAS FAIRE : DEVINER L'APPAREIL
 * ========================================================================
 *
 * Il serait tentant de présélectionner l'onglet d'après le navigateur. On ne
 * le fait pas : quelqu'un consulte souvent la page depuis son ordinateur
 * pour installer sur son téléphone, et une détection qui se trompe cache la
 * bonne réponse derrière un onglet que l'utilisateur ne pensera pas à
 * ouvrir. Le premier onglet est actif, les autres sont visibles — chacun
 * choisit.
 *
 * Structure attendue :
 *   { type: "onglets", titre?, onglets: [ { libelle, contenu?, etapes?[] } ] }
 *
 * `etapes` reprend la forme du bloc Etapes (titre, texte, astuce, image) :
 * même rendu, même vocabulaire, une seule chose à apprendre.
 */
const OngletsBloc = ({ bloc }) => {
    const onglets = Array.isArray(bloc?.onglets) ? bloc.onglets : [];
    const [actif, setActif] = useState(0);

    if (onglets.length === 0) return null;

    const courant = onglets[Math.min(actif, onglets.length - 1)] || {};
    const etapes = Array.isArray(courant.etapes) ? courant.etapes : [];

    return (
        <section className="mb-10" data-testid="bloc-onglets">
            {bloc.titre && (
                <h2 className="text-xl font-bold text-gray-900 mb-5">{bloc.titre}</h2>
            )}

            {/* role="tablist" : sans lui, un lecteur d'écran annonce une suite
                de boutons sans dire qu'ils s'excluent. */}
            <div
                role="tablist"
                className="inline-flex flex-wrap gap-1 p-1 bg-gray-100 rounded-xl mb-6"
            >
                {onglets.map((o, i) => (
                    <button
                        key={i}
                        role="tab"
                        aria-selected={i === actif}
                        onClick={() => setActif(i)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                            i === actif
                                ? 'bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white shadow-sm'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-white/60'
                        }`}
                    >
                        {o.libelle || `Onglet ${i + 1}`}
                    </button>
                ))}
            </div>

            <div role="tabpanel" className="bg-white rounded-2xl border p-5 sm:p-6">
                {courant.titre && (
                    <h3 className="font-semibold text-gray-900 text-lg">{courant.titre}</h3>
                )}
                {courant.sousTitre && (
                    <p className="text-sm text-gray-600 mt-1">{courant.sousTitre}</p>
                )}

                {courant.contenu && (
                    <p className="text-sm text-gray-600 mt-2">{courant.contenu}</p>
                )}

                {/* MEME DISPOSITION QUE LE BLOC ETAPES : numero dans la
                    pastille, icone dans son carre gris a cote du titre, une
                    carte par etape separee d'un trait. La page d'installation
                    d'origine presente ainsi ses quatre etapes, et deux rendus
                    differents pour la meme chose se remarquent. */}
                {etapes.length > 0 && (
                    <div className="divide-y divide-gray-100 border-t border-gray-100 mt-5">
                        {etapes.map((e, i) => {
                            const Icone = iconeDe(e.icone);

                            return (
                                <div key={i} className="py-6 hover:bg-gray-50 transition-colors">
                                    <div className="flex gap-4">
                                        <div className="flex-shrink-0">
                                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cabaza-purple to-cabaza-pink flex items-center justify-center text-white font-bold">
                                                {i + 1}
                                            </div>
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start gap-3 mb-2">
                                                {Icone && (
                                                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                                        <Icone className="w-4 h-4 text-gray-600" />
                                                    </div>
                                                )}
                                                {e.titre && (
                                                    <h4 className="text-lg font-semibold text-gray-900 pt-1">
                                                        {e.titre}
                                                    </h4>
                                                )}
                                            </div>

                                            {e.texte && (
                                                <p className={`text-gray-600 ${Icone ? 'ml-11' : ''}`}>
                                                    {e.texte}
                                                </p>
                                            )}

                                            {e.astuce && (
                                                <div className={`mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg ${Icone ? 'ml-11' : ''}`}>
                                                    <p className="text-sm text-amber-800">{e.astuce}</p>
                                                </div>
                                            )}

                                            {/* Voir EtapesBloc : le cadre en
                                                pointilles annonce une capture a
                                                venir et garde la hauteur stable. */}
                                            {e.image ? (
                                                <img
                                                    src={e.image.startsWith('http') || e.image.startsWith('/')
                                                        ? e.image
                                                        : `${FILES_BASE_URL}/${e.image}`}
                                                    alt=""
                                                    loading="lazy"
                                                    className={`mt-4 rounded-xl border max-w-full h-auto ${Icone ? 'ml-11' : ''}`}
                                                />
                                            ) : e.emplacementImage ? (
                                                <div className={`mt-4 p-4 bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 ${Icone ? 'ml-11' : ''}`}>
                                                    <p className="text-sm text-gray-500 text-center">
                                                        {e.emplacementImage}
                                                    </p>
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};

export default OngletsBloc;
